import { type FC, useCallback, useEffect, useMemo } from 'react'
import { Text } from 'react-common'
import { Tag2 } from '@platform/ui'
import type { ActivityRecord } from '@/api/models/activityRecord'
import type { LearnerObjectiveAccessCompactModel, LearnerObjectiveAccessFullModel } from '@/api/models/aggregate'
import type { ObjectiveRecord } from '@/api/models/objectiveRecord'
import { ArticleViewerContentRenderer } from '@/components/ActivityViewers/articleViewer/ArticleViewerContentRenderer'
import { useArticleContent } from '@/components/ActivityViewers/hooks/useArticleContent'
import { VisibilityHandler } from '@/components/VisibilityHandler/VisibilityHandler'
import { setContentHeaderPluginRightSlot } from '@/features/contentHeaderPlugin/contentHeaderPluginSlice'
import { useEventLogger } from '@/hooks/useEventLogger'
import { usePrepareCourseInfo } from '@/hooks/usePrepareCourseInfo'
import { useAppDispatch } from '@/store/hooks'
import { CourseResults } from '@/views/courses//CourseResults/CourseResults'
import { useAssignedCertificateRecords } from '@/views/courses/CourseDetails/hooks/useAssignedCertificateRecords'
import { useGradingVisibility } from '@/views/courses/hooks/useGradingVisibility'
import { CourseCover } from './CourseCover'
import styles from './CourseDetailsComponent.module.scss'
import { useFetchMyAggregates } from './hooks/useFetchMyAggregates'
import { useShowContentHeaderPlugin } from '../ActivityTypes/CompositeCourse/hooks/useShowContentHeaderPlugin'
import { CertificateDetails } from '../CertificateDetails/CertificateDetails'
import { CourseDescription } from '../CourseDescription/CourseDescription'
import { CourseInfo } from '../CourseInfo/CourseInfo'
import { useIsNotAvailable } from '../hooks/useIsNotAvailable'

const contentClassName = 'alms-article-content'

type CourseDetailsComponentProps = {
  aggregate: LearnerObjectiveAccessFullModel
  activityRecords: ActivityRecord[]
  objectiveRecords: ObjectiveRecord[]
  onStartWorkflow: (isRetakeClick?: boolean, owaId?: string) => Promise<unknown>
}

export const CourseDetailsComponent: FC<CourseDetailsComponentProps> = ({
  aggregate,
  activityRecords,
  objectiveRecords,
  onStartWorkflow,
}) => {
  useFetchMyAggregates()
  useShowContentHeaderPlugin()

  const { logEvent } = useEventLogger()

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setContentHeaderPluginRightSlot(null))
  }, [dispatch])

  const hasResults = !!objectiveRecords.length

  const objective = useMemo(() => aggregate.objective, [aggregate])

  const { isFinished } = usePrepareCourseInfo(aggregate as LearnerObjectiveAccessCompactModel)

  const resultsRecords = useMemo(
    () => objectiveRecords.map((or, i) => ({ ...or, activityRecord: activityRecords[i] })),
    [objectiveRecords, activityRecords],
  )

  const lastObjectiveResultRecord = useMemo(() => {
    const lastId = aggregate.objectiveWorkflowAggregate?.lastObjectiveRecord?.id
    if (!lastId) return null
    return resultsRecords.find((record) => record.id === lastId) || null
  }, [aggregate, resultsRecords])

  const otherResultRecords = useMemo(() => {
    if (!lastObjectiveResultRecord || !resultsRecords.length) {
      return []
    }
    if (!isFinished) {
      return resultsRecords
    }

    return resultsRecords.filter((record) => record.id !== lastObjectiveResultRecord.id) || []
  }, [lastObjectiveResultRecord, resultsRecords, isFinished])

  const { certificateRecordData, isCertificateRecordLoading } = useAssignedCertificateRecords({
    objectiveId: aggregate?.objective.id,
  })

  const mappedCertificateRecords = (certificateRecordData?.data || []).map(
    (certificateRecord) => certificateRecord.certificateTemplate.name,
  )

  const logAboutPageLoad = useCallback(() => {
    if (isCertificateRecordLoading) {
      return
    }

    logEvent('about_the_course_page_load', { objective, certificateTemplates: mappedCertificateRecords })
  }, [logEvent, objective, mappedCertificateRecords, isCertificateRecordLoading])

  const { showGrade, showPreviousAttemptScores } = useGradingVisibility(aggregate as LearnerObjectiveAccessCompactModel)
  const { canReview } = useIsNotAvailable(aggregate)

  const supportReview = !!objective?.activity?.supportReview
  const supportMobileView = !!objective?.activity?.supportMobileView

  const collections = useMemo(
    () => aggregate.objective.showcases?.map((showcase) => showcase.name),
    [aggregate.objective.showcases],
  )

  const aboutContent = useArticleContent(objective.about ?? '')

  return (
    <div className={styles.wrapper}>
      {!isCertificateRecordLoading && <VisibilityHandler onVisible={logAboutPageLoad} once />}
      <section className={styles.container}>
        <section className={styles.containerInside}>
          <CourseInfo
            aggregate={aggregate}
            onStartWorkflow={onStartWorkflow}
            hasResults={hasResults}
            supportReview={supportReview && showGrade}
          >
            <Text.Body className={styles.objectiveCode}>{objective.code}</Text.Body>
            {collections?.length ? (
              <div className={styles.collections}>
                {collections.map((collection) => (
                  <Tag2 key={collection} isActive={false} size="small" text={collection} />
                ))}
              </div>
            ) : null}
            {objective.description && <CourseDescription description={objective.description} />}
          </CourseInfo>
          <div className={styles.containerRight}>
            <CourseCover aggregate={aggregate} />
            <CourseResults
              objectiveRecords={objectiveRecords}
              otherResultRecords={otherResultRecords}
              className={styles.courseResultsContainer}
              supportReview={supportReview && showGrade && canReview}
              supportMobileView={supportMobileView}
              showPreviousAttemptScores={showPreviousAttemptScores || showGrade}
            />
          </div>
        </section>
      </section>
      <div className={styles.aboutContentContainer}>
        <section className={styles.containerInside}>
          <div className={styles.aboutContent}>
            <CertificateDetails objectiveId={aggregate.objective.id} />
            <ArticleViewerContentRenderer html={{ __html: aboutContent }} contentClassName={contentClassName} />
          </div>
        </section>
      </div>
    </div>
  )
}
